import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { 
  BookOpen, 
  Users, 
  PlusCircle, 
  TrendingUp,
  Clock,
  Star
} from 'lucide-react';
import { authContext } from '../../context/index.js';

const TeacherDashboard = () => {
  const { backendUrl } = useContext(authContext);

  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [courseIdToStudentsCount, setCourseIdToStudentsCount] = useState({});
  const [recentStudents, setRecentStudents] = useState([]);

  // Fetch creator courses
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        // Get creator's courses
        const coursesRes = await axios.get(backendUrl + '/api/course/my-courses', { withCredentials: true });
        
        if (!coursesRes.data.success) {
          console.error('Failed to fetch courses:', coursesRes.data.message);
          if (isMounted) {
            setCourses([]);
            setCourseIdToStudentsCount({});
            setRecentStudents([]);
          }
          return;
        }
        
        const courseList = coursesRes?.data?.courses || [];
        if (!isMounted) return;
        setCourses(courseList);

        // If no courses, set empty data and return early
        if (courseList.length === 0) {
          setCourseIdToStudentsCount({});
          setRecentStudents([]);
          return;
        }

        // Fetch students for each course (to compute counts and recent students)
        const studentsPromises = courseList.map(async (c) => {
          try {
            const resp = await axios.get(backendUrl + `/api/enroll/${c._id}/students`, { withCredentials: true });
            if (resp.data.success) {
              const students = resp?.data?.students || [];
              return { courseId: c._id, courseTitle: c.title, students };
            } else {
              console.warn(`Failed to fetch students for course ${c._id}:`, resp.data.message);
              return { courseId: c._id, courseTitle: c.title, students: [] };
            }
          } catch (error) {
            console.warn(`Error fetching students for course ${c._id}:`, error.message);
            return { courseId: c._id, courseTitle: c.title, students: [] };
          }
        });

        const studentsResults = await Promise.all(studentsPromises);
        if (!isMounted) return;

        const idToCount = {};
        const flatStudents = [];
        studentsResults.forEach(({ courseId, courseTitle, students }) => {
          idToCount[courseId] = students.length;
          // backend doesn't provide enrolledAt; we still show recent list by slicing first few
          students.slice(0, 5).forEach((s) => {
            flatStudents.push({
              name: s?.name || 'Student',
              email: s?.email || '',
              courseTitle,
              // no enrolled time available
            });
          });
        });
        setCourseIdToStudentsCount(idToCount);
        // Limit to top 5
        setRecentStudents(flatStudents.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (isMounted) {
          setCourses([]);
          setCourseIdToStudentsCount({});
          setRecentStudents([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [backendUrl]);

  const stats = useMemo(() => {
    const totalCourses = courses.length;
    const totalStudents = Object.values(courseIdToStudentsCount).reduce((a, b) => a + b, 0);
    // Placeholder for now: this month courses (client-side based on createdAt)
    const now = new Date();
    const thisMonth = courses.filter((c) => {
      if (!c?.createdAt) return false;
      const d = new Date(c.createdAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    // Ratings not implemented on backend
    const avgRating = '-';

    return [
      { title: 'Total Courses', value: String(totalCourses), icon: BookOpen, color: 'text-blue-600' },
      { title: 'Total Students', value: String(totalStudents), icon: Users, color: 'text-green-600' },
      { title: 'This Month', value: String(thisMonth), icon: TrendingUp, color: 'text-purple-600' },
      { title: 'Avg Rating', value: String(avgRating), icon: Star, color: 'text-yellow-600' },
    ];
  }, [courses, courseIdToStudentsCount]);

  const recentCourses = useMemo(() => {
    const sorted = [...courses].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
    return sorted.slice(0, 3).map((c) => ({
      id: c._id,
      title: c.title,
      students: courseIdToStudentsCount[c._id] || 0,
      rating: '-',
      status: c.published ? 'Published' : 'Draft',
      lastUpdated: c.updatedAt ? new Date(c.updatedAt).toLocaleString() : new Date(c.createdAt).toLocaleString(),
    }));
  }, [courses, courseIdToStudentsCount]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Dashboard</h1>
          <p className="text-secondary-600 mt-1">Welcome back! Here's what's happening with your courses.</p>
        </div>
        <Link to="/teacher/courses/create">
          <Button className="flex items-center space-x-2">
            <PlusCircle size={20} />
            <span>Create Course</span>
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} padding="sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-secondary-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-secondary-900 mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg bg-gray-50 ${stat.color}`}>
                  <Icon size={24} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Courses */}
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <Card.Title>Recent Courses</Card.Title>
              <Link to="/teacher/courses" className="text-sm text-primary-600 hover:text-primary-700">
                View all
              </Link>
            </div>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              {recentCourses.map((course) => (
                <div key={course.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-secondary-900">{course.title}</h4>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-secondary-600">
                      <span className="flex items-center">
                        <Users size={16} className="mr-1" />
                        {course.students} students
                      </span>
                      <span className="flex items-center">
                        <Star size={16} className="mr-1" />
                        {course.rating}
                      </span>
                      <span className="flex items-center">
                        <Clock size={16} className="mr-1" />
                        {course.lastUpdated}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      course.status === 'Published' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {course.status}
                    </span>
                  </div>
                </div>
              ))}
              {recentCourses.length === 0 && (
                <div className="text-sm text-secondary-600">No courses yet. Create your first course to get started.</div>
              )}
            </div>
          </Card.Content>
        </Card>

        {/* Recent Students */}
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <Card.Title>Recent Students</Card.Title>
              <Link to="/teacher/students" className="text-sm text-primary-600 hover:text-primary-700">
                View all
              </Link>
            </div>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              {recentStudents.map((student, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-600">
                        {student.name?.charAt(0) || 'S'}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-medium text-secondary-900">{student.name}</h4>
                      <p className="text-sm text-secondary-600">{student.courseTitle}</p>
                    </div>
                  </div>
                  {/* enrolledAt not available in backend response */}
                </div>
              ))}
              {recentStudents.length === 0 && (
                <div className="text-sm text-secondary-600">No students yet. Share your course to get enrollments.</div>
              )}
            </div>
          </Card.Content>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <Card.Header>
          <Card.Title>Quick Actions</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/teacher/courses/create">
              <div className="p-4 border border-secondary-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <PlusCircle className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-secondary-900">Create New Course</h3>
                    <p className="text-sm text-secondary-600">Start building your next course</p>
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/teacher/courses">
              <div className="p-4 border border-secondary-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <BookOpen className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-secondary-900">Manage Courses</h3>
                    <p className="text-sm text-secondary-600">Edit and organize your courses</p>
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/teacher/students">
              <div className="p-4 border border-secondary-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors cursor-pointer">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-secondary-900">View Students</h3>
                    <p className="text-sm text-secondary-600">See who's enrolled in your courses</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default TeacherDashboard;
